import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectLoadingPasscodeLists, selectPasscodeLists } from '@/store/slices/audiences/selectors';
import CustomSelect from '@ui/select/custom-select/custom-select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IScheduledEmail } from '@/types/email.types';
import type { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { patchScheduledEmailFields } from '@/store/slices/scheduled-emails';
import Container from '@ui/container/container';
import useLoadPasscodeLists from '@/micros/homepage/audiences/utils/use-load-passcode-lists';
import Switch from '@ui/switch/switch';
import Text from '@ui/text/text/text';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';

interface Props {
  scheduledEmail: IScheduledEmail | undefined;
}
function AudienceInput({ scheduledEmail }: Props) {
  const audiences = useAppSelector(selectPasscodeLists);
  const loading = useAppSelector(selectLoadingPasscodeLists);
  useLoadPasscodeLists();
  const [sendToAll, setSendToAll] = useState(true);
  const [selected, setSelected] = useState<TReactSelectOption[]>([]);
  const initialized = useRef(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!initialized.current && scheduledEmail) {
      setSendToAll(scheduledEmail.audiences.length === 0);
      setSelected(scheduledEmail.audiences);
      initialized.current = true;
    }
  }, [scheduledEmail]);

  const handleChange = useCallback(
    (options: TReactSelectOption[]) => {
      if (!scheduledEmail?.uuid) return;

      setSelected(options.map((opt) => ({ label: opt.label, value: String(opt.value) })));

      dispatch(
        patchScheduledEmailFields({
          uuid: scheduledEmail.uuid,
          fields: {
            audiences: options.map((opt) => ({ label: opt.label, value: String(opt.value) })),
          },
        })
      );
    },
    [dispatch, scheduledEmail?.uuid]
  );

  const options = useMemo(() => {
    if (audiences) {
      return audiences.map((audience) => ({ label: audience.name, value: audience.uuid }));
    }

    return [];
  }, [audiences]);

  const toggleAll = useCallback(
    (_: unknown, isOn: boolean) => {
      if (!scheduledEmail) return;

      if (isOn) {
        setSendToAll(isOn);
        dispatch(
          patchScheduledEmailFields({
            uuid: scheduledEmail.uuid,
            fields: {
              audiences: [],
            },
          })
        );
      } else {
        setSendToAll(false);
        dispatch(
          patchScheduledEmailFields({
            uuid: scheduledEmail.uuid,
            fields: {
              audiences: selected.map((opt) => ({ label: opt.label, value: String(opt.value) })),
            },
          })
        );
      }
    },
    [dispatch, scheduledEmail, selected]
  );

  return (
    <Container padding="0 12px">
      <Container
        design="GlobalCont2"
        padding="12px 16px"
        style={{ transition: 'border-radius 350ms' }}
        borderRadius={sendToAll ? '16px' : '12px'}
      >
        <Text className="H6" design="GlobalIcoText1">
          Audiences
        </Text>
        <Container
          design="GlobalCont3"
          padding="12px"
          borderRadius="12px"
          className="flex ai-center jc-space-between margin-t-12"
        >
          <Text className="L1">Send to all profiles</Text>
          <Switch value="all" on={sendToAll} onClick={toggleAll} />
        </Container>
        <SlideDownCard autoGrow open={!sendToAll} animateOnInitialLoad={false}>
          <CustomSelect
            id="audience-input"
            containerClassName="margin-t-12"
            Placeholder="Select audiences"
            options={options}
            controlledOptions={selected}
            onChange={handleChange}
            disabled={loading || !scheduledEmail}
            filterSelected
            inputPadding="0px 14px"
            clearInputOnMouseSelect
            isMultiSelect
            direction={'vertical'}
            maxRows={12}
            hideHeader
          />
        </SlideDownCard>
      </Container>
    </Container>
  );
}

export default AudienceInput;
